import { useState } from 'react';
import { comparisonColor, comparisonPositive } from '../../helpers/Custom';
import { Dashboard, KeyCountWrapper } from '../../models/ApiModels';
import { ToggleButton } from '../../utils/ToggleButton';
import PieChart from './PieChart';
interface Props {
  dashboardData: Dashboard;
  compare: boolean;
}

function DashBoardDataLicenses(props: Props) {
  const [isLegacy, setIsLegacy] = useState<boolean>(false);

  const notActivatedLights: KeyCountWrapper = {
    count: props.dashboardData.systems.totalAmountOfSystems.count - props.dashboardData.systems.underwaterLights.count,
    key: 'Not activated',
    diff: props.dashboardData.systems.totalAmountOfSystems.diff - props.dashboardData.systems.underwaterLights.diff,
  };

  const upgradedToMap = [
    {
      label: 'Ultimate',
      className: 'prose-heading4 text-ultimate-orange-100',
      data: props.dashboardData.licenses.trialsUpgradedToUltimate,
    },
    {
      label: 'Cruise',
      className: 'prose-heading4 text-gray-80',
      data: props.dashboardData.licenses.trialsUpgradedToCruise,
    },
    {
      label: 'Other',
      className: 'text-gray-80',
      data: props.dashboardData.licenses.trialsUpgradedToOther,
    },
    {
      label: 'Not upgraded',
      className: 'text-gray-80',
      data: props.dashboardData.licenses.trialsNotUpgraded,
    },
  ];

  const functionPackagesOverviewToMap = [
    {
      label: 'Ultimate',
      className: 'prose-heading3 text-ultimate-orange-100',
      data: props.dashboardData.systems.ultimatePercentage,
      suffix: '%',
    },
    {
      label: 'Cruise',
      className: 'prose-heading3 text-gray-80',
      data: props.dashboardData.systems.cruisePercentage,
      suffix: '%',
    },
    {
      label: 'Manual',
      className: 'prose-heading3 text-gray-40',
      data: props.dashboardData.systems.manualPercentage,
      suffix: '%',
    },
  ];

  const trialOverviewToMap = [
    {
      label: 'Total trials',
      className: 'prose-heading3',
      data: props.dashboardData.licenses.totalTrials,
    },
    {
      label: 'Active trials',
      className: 'prose-heading3 text-primary-100',
      data: props.dashboardData.licenses.totalActiveTrials,
    },
  ];

  return (
    <>
      <div className='mb-5 mt-10'>
        <div className='flex items-center justify-between'>
          <span className='prose-heading4'>Licenses</span>
          <ToggleButton toggled={!isLegacy} onClick={() => setIsLegacy(!isLegacy)} label='Exclude legacy licences' />
        </div>
      </div>

      <div className='md:flex md:flex-wrap flex-1 w-full gap-4'>
        <div className='bg-gray-3 pt-4 mb-4 md:mb-0 flex-1 flex flex-col px-8'>
          <div className='text-center mt-4'>Function packages</div>
          {isLegacy ? (
            <PieChart
              compare={props.compare}
              series={[
                props.dashboardData.systems.manual,
                props.dashboardData.systems.comfort,
                props.dashboardData.systems.comfortPlus,
                props.dashboardData.systems.dynamic,
                props.dashboardData.systems.dynamicPlus,
                props.dashboardData.systems.cruise,
                props.dashboardData.systems.ultimate,
              ]}
              colors={['#CDCFCF', '#B3DEE1', '#80D1D8', '#4DC4CF', '#06C0D4', '#394041', '#F56432']}
              labels={['Manual', 'Comfort', 'Comfort Plus', 'Dynamic', 'Dynamic Plus', 'Cruise', 'Ultimate']}
              showMarkers={true}
            />
          ) : (
            <>
              <div className='flex-1'>
                <PieChart
                  compare={props.compare}
                  series={[
                    props.dashboardData.systems.manual,
                    props.dashboardData.systems.cruise,
                    props.dashboardData.systems.ultimate,
                  ]}
                  colors={['#CDCFCF', '#394041', '#F56432']}
                  labels={['Manual', 'Cruise', 'Ultimate']}
                  showMarkers={true}
                />
              </div>
              <div className='w-full max-w-3xl mx-auto flex justify-center gap-8 md:gap-16 mt-auto pb-8'>
                {props.dashboardData &&
                  functionPackagesOverviewToMap.map((item) => (
                    <div key={item.label} className='text-center flex-1 min-w-0'>
                      <div>{item.label}</div>
                      <h3 className={item.className}>
                        {item.data.count}
                        {item.suffix}
                      </h3>
                      {props.compare && (
                        <span className={`prose-heading4 ${comparisonColor(item.data.diff)}`}>
                          {comparisonPositive(item.data.diff) + item.data.diff}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>

        {isLegacy ? (
          <div className='bg-gray-3 pt-4 mb-4 md:mb-0 flex-1 flex flex-col px-8'>
            <div className='text-center mt-4'>Underwater lights</div>
            <PieChart
              compare={props.compare}
              series={[props.dashboardData.systems.underwaterLights, notActivatedLights]}
              colors={['#06C0D5', '#646868']}
              labels={['Activated', 'Not activated']}
              showMarkers={true}
            />
          </div>
        ) : (
          <div className='bg-gray-3 pt-4 mb-4 md:mb-0 flex-1 flex flex-col px-8'>
            <div className='text-center mt-4 mb-6'>Function trials</div>
            <div className='flex flex-col mb-8 flex-1'>
              <>
                <div>Upgraded to</div>
                <div className='flex justify-between flex-col w-full gap-2 mt-2'>
                  {upgradedToMap.map((item) => (
                    <div key={item.label} className='flex items-center justify-between gap-8'>
                      <span className={item.className}>{item.label}</span>
                      <span className='flex gap-4 items-center'>
                        <span>{item.data.count}</span>
                        {props.compare && (
                          <span className={`min-w-[2rem] ${comparisonColor(item.data.diff)}`}>
                            {comparisonPositive(item.data.diff) + item.data.diff}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            </div>
            <div className='w-full max-w-3xl mx-auto flex justify-center gap-8 md:gap-16 mt-auto pb-8'>
              {trialOverviewToMap.map((item) => (
                <div key={item.label} className='text-center flex-1 min-w-0'>
                  <div>{item.label}</div>
                  <h3 className={item.className}>{item.data.count}</h3>
                  {props.compare && (
                    <span className={`prose-heading4 ${comparisonColor(item.data.diff)}`}>
                      {comparisonPositive(item.data.diff) + item.data.diff}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default DashBoardDataLicenses;
